/**
 * OpenClaw Gateway WebSocket 客户端
 *
 * 用法示例：
 *   const client = new ClawWsClient({ url: 'wss://...', token: '...' });
 *   client.on('chat', (payload) => console.log(payload.delta));
 *   await client.connect();
 *   await client.request('chat.send', { ... });
 */

import type {
  ChatEventPayload,
  ConnectParams,
  EventPayloads,
  GatewayEvent,
  GatewayFrame,
  HelloOkPayload,
  MethodParams,
  MethodResults,
} from './protocol';

// ─── 配置 ─────────────────────────────────────────────────────────────────────

export interface ClawWsClientOptions {
  /** WebSocket 服务地址，例如 ws://localhost:18789 或 wss://example.com */
  url: string;
  /** 鉴权 Token，对应 connect.params.auth.token */
  token?: string;
  /** 客户端标识，默认 mario-web */
  clientId?: string;
  /** 客户端版本，默认 1.0.0 */
  clientVersion?: string;
  /** 协议版本范围，默认 [3, 3] */
  protocol?: { min: number; max: number };
  /** 断线自动重连，默认 true */
  autoReconnect?: boolean;
  /** 重连最大次数，默认 5；设为 0 禁用 */
  maxReconnectAttempts?: number;
  /** 重连基础延迟（ms），默认 1000，指数退避 */
  reconnectBaseDelay?: number;
  /** 单次请求超时（ms），默认 30000 */
  requestTimeout?: number;
  /** 应用层心跳间隔（ms），默认 30000；设为 0 禁用 */
  heartbeatInterval?: number;
  /** 请求的权限范围，默认 ['operator.read', 'operator.write'] */
  scopes?: string[];
  /** 客户端的能力声明，默认 [] */
  caps?: string[];
  /** 客户端的命令声明，默认 [] */
  commands?: string[];
}

// ─── 内部类型 ─────────────────────────────────────────────────────────────────

type PendingRequest<T = unknown> = {
  resolve: (payload: T) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

type EventListenerMap = {
  [E in GatewayEvent]?: Array<(payload: EventPayloads[E]) => void>;
};

type ClientEventListenerMap = {
  /** WebSocket 已连接并完成 hello-ok 握手 */
  connected: Array<(helloOk: HelloOkPayload) => void>;
  /** WebSocket 断开 */
  disconnected: Array<(event: CloseEvent | Event) => void>;
  /** 底层错误 */
  error: Array<(err: Event | Error) => void>;
};

export type RawFrameHandler = (frame: GatewayFrame) => void;

// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_OPTS: Required<
  Omit<ClawWsClientOptions, 'url' | 'token' | 'clientId' | 'clientVersion' | 'scopes' | 'caps' | 'commands'>
> = {
  protocol: { min: 3, max: 3 },
  autoReconnect: true,
  maxReconnectAttempts: 5,
  reconnectBaseDelay: 1000,
  requestTimeout: 30_000,
  heartbeatInterval: 30_000,
};

let _idCounter = 0;
// 随机生成一个 8位字母和数字的组合
const genUuid = () => Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
const genId = () => `mario-${genUuid()}-${++_idCounter}`;

// ─────────────────────────────────────────────────────────────────────────────

export class ClawWsClient {
  private opts: Required<Omit<ClawWsClientOptions, 'scopes'>> & Pick<ClawWsClientOptions, 'scopes'>;
  private ws: WebSocket | null = null;
  private pending = new Map<string, PendingRequest>();
  private gatewayListeners: EventListenerMap = {};
  private clientListeners: ClientEventListenerMap = {
    connected: [],
    disconnected: [],
    error: [],
  };
  private reconnectAttempts = 0;
  private _connecting: Promise<HelloOkPayload> | null = null;
  private _helloOk: HelloOkPayload | null = null;
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _rawFrameHandlers: RawFrameHandler[] = [];

  constructor(opts: ClawWsClientOptions) {
    this.opts = {
      clientId: 'openclaw-macos',
      clientVersion: '1.0.0',
      caps: ['tool-events', 'structured-commands', 'agent-events, thinking-events'],
      commands: [],
      token: '',
      protocol: { min: 3, max: 3 },
      autoReconnect: DEFAULT_OPTS.autoReconnect,
      maxReconnectAttempts: DEFAULT_OPTS.maxReconnectAttempts,
      reconnectBaseDelay: DEFAULT_OPTS.reconnectBaseDelay,
      requestTimeout: DEFAULT_OPTS.requestTimeout,
      heartbeatInterval: DEFAULT_OPTS.heartbeatInterval,
      ...opts,
    };
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  /** 建立 WebSocket 连接并完成握手，返回 hello-ok payload */
  connect(): Promise<HelloOkPayload> {
    if (this._connecting) return this._connecting;
    this._connecting = this._doConnect().finally(() => {
      this._connecting = null;
    });
    return this._connecting;
  }

  /** 主动断开连接（不触发自动重连） */
  disconnect() {
    this.opts.autoReconnect = false;
    this._stopHeartbeat();
    this.ws?.close(1000, 'client disconnect');
    this.ws = null;
  }

  /**
   * 发送方法请求并等待响应
   * @param method  方法名
   * @param params  方法参数
   */
  request<M extends keyof MethodParams>(
    method: M,
    params?: MethodParams[M],
  ): Promise<MethodResults[M]>;
  request<M extends keyof MethodParams>(method: M, params?: unknown): Promise<unknown> {
    const id = genId();
    return new Promise((resolve, reject) => {
      // 如果不存在 ws 或者 ws 未连接，则重连
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.connect().then()
        reject(new Error(`[ClawWs] WebSocket not connected (method: ${method})`));
        return;
      }
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`[ClawWs] Request timeout (method: ${method}, id: ${id})`));
      }, this.opts.requestTimeout);

      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer,
      });

      this._send({ type: 'req', id, method, params: params as Record<string, unknown> });
    });
  }

  /**
   * 订阅服务端推送事件
   * @param event   事件名
   * @param handler 回调
   */
  on<E extends GatewayEvent>(event: E, handler: (payload: EventPayloads[E]) => void): this {
    if (!this.gatewayListeners[event]) {
      (this.gatewayListeners as Record<string, unknown[]>)[event] = [];
    }
    (this.gatewayListeners[event] as Array<typeof handler>).push(handler);
    return this;
  }

  /** 取消订阅事件 */
  off<E extends GatewayEvent>(event: E, handler: (payload: EventPayloads[E]) => void): this {
    const list = this.gatewayListeners[event] as Array<typeof handler> | undefined;
    if (list) {
      const idx = list.indexOf(handler);
      if (idx !== -1) list.splice(idx, 1);
    }
    return this;
  }

  /** 订阅客户端状态事件（connected / disconnected / error） */
  onClient<E extends keyof ClientEventListenerMap>(
    event: E,
    handler: ClientEventListenerMap[E][number],
  ): this {
    (this.clientListeners[event] as Array<typeof handler>).push(handler);
    return this;
  }

  /** 取消订阅客户端状态事件 */
  offClient<E extends keyof ClientEventListenerMap>(
    event: E,
    handler: ClientEventListenerMap[E][number],
  ): this {
    const list = this.clientListeners[event] as Array<typeof handler>;
    const idx = list.indexOf(handler);
    if (idx !== -1) list.splice(idx, 1);
    return this;
  }

  /** hello-ok 信息（握手前为 null） */
  get helloOk(): HelloOkPayload | null {
    return this._helloOk;
  }

  /** 当前连接是否已就绪 */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this._helloOk !== null;
  }

  /** 订阅所有收到的原始帧（调试用） */
  onRawFrame(handler: RawFrameHandler): this {
    this._rawFrameHandlers.push(handler);
    return this;
  }

  /** 取消订阅原始帧 */
  offRawFrame(handler: RawFrameHandler): this {
    const idx = this._rawFrameHandlers.indexOf(handler);
    if (idx !== -1) this._rawFrameHandlers.splice(idx, 1);
    return this;
  }

  // ─── 快捷方法 ──────────────────────────────────────────────────────────────

  /** 发送聊天消息，返回第一条 assistant 事件；流式内容通过 on('chat') 监听 */
  sendChat(
    params: MethodParams['chat.send'],
    onChunk?: (chunk: ChatEventPayload) => void,
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const handler = (payload: ChatEventPayload) => {
        // 服务端可能在客户端 UUID 前添加前缀，如 'agent:main:<uuid>'，需兼容两种格式
        const keysMatch =
          payload.sessionKey === params.sessionKey ||
          payload.sessionKey.endsWith(`:${params.sessionKey}`);
        if (!keysMatch) return;
        onChunk?.(payload);
        if (payload.state !== 'delta') {
          this.off('chat', handler);
          resolve();
        }
      };
      this.on('chat', handler);
      try {
        await this.request('chat.send', params);
      } catch (err) {
        this.off('chat', handler);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _doConnect(): Promise<HelloOkPayload> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.opts.url);
      this.ws = ws;

      ws.onopen = () => {
        this._sendConnectReq();
      };

      ws.onmessage = (ev) => {
        let frame: GatewayFrame;
        try {
          frame = JSON.parse(String(ev.data)) as GatewayFrame;
        } catch {
          return;
        }
        this._handleFrame(frame, resolve, reject);
      };

      ws.onclose = (ev) => {
        this._helloOk = null;
        this._stopHeartbeat();
        this.clientListeners.disconnected.forEach((h) => h(ev));
        this._scheduleReconnect();
        reject(new Error(`[ClawWs] WebSocket closed: ${ev.code} ${ev.reason}`));
      };

      ws.onerror = (ev) => {
        console.log('ws.onerror', ev);
        this.clientListeners.error.forEach((h) => h(ev));
        reject(new Error('[ClawWs] WebSocket error'));
      };
    });
  }

  private _sendConnectReq() {
    const { clientId, clientVersion, token, protocol, scopes, caps } = this.opts;
    const params: ConnectParams = {
      minProtocol: protocol.min,
      maxProtocol: protocol.max,
      client: {
        id: clientId,
        displayName: clientId,
        version: clientVersion,
        platform: 'web',
        // mode 与 role 是两个独立字段：mode 描述客户端 UI 形态，role 描述权限角色
        mode: 'ui',
      },
      caps: ['tool-events'],
      role: 'operator',
      scopes: ['operator.read', 'operator.write', "operator.admin", "operator.approvals", "operator.pairing"],
      ...(token ? { auth: { token } } : {}),
    };
    this._send({ type: 'req', id: 'connect-0', method: 'connect', params });
  }

  private _handleFrame(
    frame: GatewayFrame,
    onHello: (helloOk: HelloOkPayload) => void,
    onHelloErr: (err: Error) => void,
  ) {
    this._rawFrameHandlers.forEach((h) => h(frame));

    if (frame.type === 'res') {
      // hello-ok
      if (frame.id === 'connect-0') {
        if (frame.ok) {
          const helloOk = frame.payload as HelloOkPayload;
          this._helloOk = helloOk;
          this.reconnectAttempts = 0;
          this._startHeartbeat();
          this.clientListeners.connected.forEach((h) => h(helloOk));
          onHello(helloOk);
        } else {
          onHelloErr(
            new Error(
              `[ClawWs] Handshake rejected: ${JSON.stringify((frame as { error?: unknown }).error)}`,
            ),
          );
        }
        return;
      }
      // 普通请求响应
      const pending = this.pending.get(frame.id);
      if (pending) {
        clearTimeout(pending.timer);
        this.pending.delete(frame.id);
        if (frame.ok) {
          pending.resolve(frame.payload);
        } else {
          pending.reject(
            new Error(
              `[ClawWs] Request failed: ${JSON.stringify((frame as { error?: unknown }).error)}`,
            ),
          );
        }
      }
      return;
    }

    if (frame.type === 'event') {
      const eventName = frame.event as GatewayEvent;
      const handlers = this.gatewayListeners[eventName];
      if (handlers) {
        (handlers as Array<(p: unknown) => void>).forEach((h) => h(frame.payload));
      }
    }
  }

  private _send(frame: Omit<GatewayFrame, 'ok' | 'payload' | 'error'> & Record<string, unknown>) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }

  private _startHeartbeat() {
    this._stopHeartbeat();
    const interval = this.opts.heartbeatInterval;
    if (!interval || interval <= 0) return;
    this._heartbeatTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      this.request('health', {}).catch(() => {
        // 心跳失败，主动关闭触发重连
        this.ws?.close(4000, 'heartbeat timeout');
      });
    }, interval);
  }

  private _stopHeartbeat() {
    if (this._heartbeatTimer !== null) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
  }

  private _scheduleReconnect() {
    if (!this.opts.autoReconnect) return;
    if (this.opts.maxReconnectAttempts > 0 && this.reconnectAttempts >= this.opts.maxReconnectAttempts) {
      return;
    }
    const delay = this.opts.reconnectBaseDelay * 2 ** this.reconnectAttempts;
    this.reconnectAttempts++;
    setTimeout(() => {
      this.connect().catch(() => {/* 重连失败会继续调度 */});
    }, delay);
  }
}
